import { useCallback, useEffect, useMemo, useState } from 'react';
import { PageShell } from '../layout/page-shell.jsx';
import { api, ApiError } from '../../api/index.js';
import { useToast } from '../ui/toast.jsx';

/**
 * Etap 2 Milestone E (2026-07-11) — RPC console.
 *
 * v1.19 UX rework — no more free-form JSON args field. Every method is
 * a fully-formed dropdown entry with its own pre-baked wire payload.
 * `state.get` fans out into ten separate picker entries — one per
 * feature — grouped visually with <optgroup>. The composer preview
 * shows exactly what will leave the coord over the wire, so the admin
 * can eyeball the request before they Dispatch it.
 *
 * Method registry lives on the terminal (`TmsRpcDispatcher.Method`).
 * The METHODS array below is a picker mirror: each entry declares
 * `wireMethod` (what the terminal receives) + optional `wireArgs`
 * (JSON args pre-baked). New methods added on the terminal show up on
 * this page after we extend METHODS.
 */

const METHODS = [
  // ─────────── Diagnostic (no args) ───────────
  {
    id: 'ping',
    group: 'Diagnostic',
    wireMethod: 'ping',
    label: 'ping',
    tagline: 'Round-trip sanity check',
    icon: 'fa-stopwatch',
    tone: 'sky',
    typicalMs: '< 500',
    returns: 'pong, ts, uptimeMs, versionName, androidSdk',
  },
  {
    id: 'system.info',
    group: 'Diagnostic',
    wireMethod: 'system.info',
    label: 'system.info',
    tagline: 'Build + hardware fingerprint',
    icon: 'fa-mobile-screen-button',
    tone: 'sky',
    typicalMs: '< 500',
    returns: 'App version, Android SDK/release, manufacturer, model, hardware id, heap usage',
  },
  {
    id: 'db.stats',
    group: 'Diagnostic',
    wireMethod: 'db.stats',
    label: 'db.stats',
    tagline: 'DB family size + caches on disk',
    icon: 'fa-database',
    tone: 'sky',
    typicalMs: '< 1.5 s',
    returns: 'Encrypted H2 DB size + file count, layers cache, attachments cache',
  },
  {
    id: 'cert.info',
    group: 'Diagnostic',
    wireMethod: 'cert.info',
    label: 'cert.info',
    tagline: 'mTLS cert renewal state',
    icon: 'fa-shield-halved',
    tone: 'sky',
    typicalMs: '< 500',
    returns: 'Cert serial (hex), notAfter (epoch ms), daysRemaining, renewal state',
  },
  {
    id: 'retention.sweep',
    group: 'Diagnostic',
    wireMethod: 'retention.sweep',
    label: 'retention.sweep',
    tagline: 'Kick the retention janitor now',
    icon: 'fa-broom',
    tone: 'amber',
    typicalMs: '< 2 s',
    returns: 'swept:true — verify DB shrink via db.stats afterwards',
  },

  // ─────────── state.get: infrastructure ───────────
  {
    id: 'state.get:vpn',
    group: 'state.get — infrastructure',
    wireMethod: 'state.get',
    wireArgs: { feature: 'vpn' },
    label: 'vpn',
    tagline: 'WireGuard tunnel status',
    icon: 'fa-lock',
    tone: 'emerald',
    typicalMs: '< 500',
    returns: 'state (UP / DOWN / CONNECTING / …), hasProfile, enabled toggle',
  },
  {
    id: 'state.get:cert',
    group: 'state.get — infrastructure',
    wireMethod: 'state.get',
    wireArgs: { feature: 'cert' },
    label: 'cert',
    tagline: 'Same payload as cert.info method',
    icon: 'fa-shield-halved',
    tone: 'emerald',
    typicalMs: '< 500',
    returns: 'Cert serial + notAfter + daysRemaining + renewal state',
  },
  {
    id: 'state.get:biometrics',
    group: 'state.get — infrastructure',
    wireMethod: 'state.get',
    wireArgs: { feature: 'biometrics' },
    label: 'biometrics',
    tagline: 'Biometric login availability + mode',
    icon: 'fa-fingerprint',
    tone: 'emerald',
    typicalMs: '< 500',
    returns: 'Fingerprint / face enrollment + STRONG / WEAK / CONVENIENCE class',
  },
  {
    id: 'state.get:spoofing',
    group: 'state.get — infrastructure',
    wireMethod: 'state.get',
    wireArgs: { feature: 'spoofing' },
    label: 'spoofing',
    tagline: 'GNSS spoofing detector state',
    icon: 'fa-satellite-dish',
    tone: 'emerald',
    typicalMs: '< 500',
    returns: 'Per-source SAT/LTE state, fired detectors D1..D11, chip family calibration',
  },
  {
    id: 'state.get:opsec',
    group: 'state.get — infrastructure',
    wireMethod: 'state.get',
    wireArgs: { feature: 'opsec' },
    label: 'opsec',
    tagline: 'Operator mode + sound/vibrate policy',
    icon: 'fa-user-shield',
    tone: 'emerald',
    typicalMs: '< 500',
    returns: 'Current mode (NORMAL / ALERT / STEALTH / OPSEC / SILENT / MAYDAY) + per-category sound + vibrate',
  },

  // ─────────── state.get: environment ───────────
  {
    id: 'state.get:location.source',
    group: 'state.get — environment',
    wireMethod: 'state.get',
    wireArgs: { feature: 'location.source' },
    label: 'location.source',
    tagline: 'Coordinate source: auto / satellite / cellular / manual',
    icon: 'fa-location-crosshairs',
    tone: 'indigo',
    typicalMs: '< 500',
    returns: 'Current source enum + manual lat/lon if MANUAL is picked',
  },
  {
    id: 'state.get:role',
    group: 'state.get — environment',
    wireMethod: 'state.get',
    wireArgs: { feature: 'role' },
    label: 'role',
    tagline: 'Auto-detected role + capabilities',
    icon: 'fa-diagram-project',
    tone: 'indigo',
    typicalMs: '< 500',
    returns: 'BORDER_ROUTER / GATEWAY / REPEATER / EDGE / OPERATOR + wan/mesh/lora capabilities',
  },
  {
    id: 'state.get:battery',
    group: 'state.get — environment',
    wireMethod: 'state.get',
    wireArgs: { feature: 'battery' },
    label: 'battery',
    tagline: 'Battery + power source + thermal',
    icon: 'fa-battery-half',
    tone: 'indigo',
    typicalMs: '< 500',
    returns: 'Percent, charging/discharging, source (usb/ac/wireless), health, temperature °C, voltage, low-power mode, thermal status',
  },
  {
    id: 'state.get:network',
    group: 'state.get — environment',
    wireMethod: 'state.get',
    wireArgs: { feature: 'network' },
    label: 'network',
    tagline: 'Active WAN + throughput baseline',
    icon: 'fa-network-wired',
    tone: 'indigo',
    typicalMs: '< 500',
    returns: 'Active transport (wifi/cell/eth/vpn/bt), total + uid RX/TX bytes, link Kbps, isMetered, hasInternet, hasValidated',
  },
  {
    id: 'state.get:transports',
    group: 'state.get — environment',
    wireMethod: 'state.get',
    wireArgs: { feature: 'transports' },
    label: 'transports',
    tagline: 'All 7 transports enumerate',
    icon: 'fa-tower-broadcast',
    tone: 'indigo',
    typicalMs: '< 500',
    returns: 'hwPresent + operatorToggled + active for LTE / Wi-Fi AP / Wi-Fi P2P / Wi-Fi P2P multi / Bluetooth / DMR / Tor',
  },

  // ─────────── state.get: routing ───────────
  {
    id: 'state.get:routing',
    group: 'state.get — routing',
    wireMethod: 'state.get',
    wireArgs: { feature: 'routing' },
    label: 'routing',
    tagline: 'Path table + persona status dump',
    icon: 'fa-diagram-project',
    tone: 'violet',
    typicalMs: '< 500',
    returns: 'own {personId, terminalUuid, homeNodeId, isAnchored, connectionState} + paths[] (nextHopContactId, transport, hopCount, lastSeenMs, lastSuccessMs, successCount, failCount, avgLatencyMs, reliability, ageMs) + personaStatus[] (personId, lastAnchoredAsOfMs, lastAnchoredHomeNodeId, lastSeenAsOfMs, anchoredAgeMs, seenAgeMs)',
  },
];

// Group the flat list by `group` so the <select> can render <optgroup>s
// in a single stable pass without doing it inline on every render.
function groupMethods(list) {
  const seen = new Map();
  for (const m of list) {
    if (!seen.has(m.group)) seen.set(m.group, []);
    seen.get(m.group).push(m);
  }
  return Array.from(seen.entries()).map(([label, items]) => ({ label, items }));
}

const METHOD_GROUPS = groupMethods(METHODS);

// Tone-to-Tailwind mapping for the tiny "chip" element to the left of
// method labels + used to tint the method description card. Keeping
// the map inline avoids Tailwind's purge dropping dynamic class names.
const TONES = {
  sky: {
    chip:    'bg-sky-100 text-sky-700 ring-sky-200',
    card:    'border-sky-200 bg-sky-50/70',
    accent:  'text-sky-700',
  },
  amber: {
    chip:    'bg-amber-100 text-amber-800 ring-amber-200',
    card:    'border-amber-200 bg-amber-50/70',
    accent:  'text-amber-700',
  },
  emerald: {
    chip:    'bg-emerald-100 text-emerald-700 ring-emerald-200',
    card:    'border-emerald-200 bg-emerald-50/70',
    accent:  'text-emerald-700',
  },
  indigo: {
    chip:    'bg-indigo-100 text-indigo-700 ring-indigo-200',
    card:    'border-indigo-200 bg-indigo-50/70',
    accent:  'text-indigo-700',
  },
  violet: {
    chip:    'bg-violet-100 text-violet-700 ring-violet-200',
    card:    'border-violet-200 bg-violet-50/70',
    accent:  'text-violet-700',
  },
};

function toneOf(m) { return TONES[m?.tone] || TONES.sky; }

function MethodDescriptionCard({ method }) {
  if (!method) return null;
  const tone = toneOf(method);
  return (
    <div className={`rounded-2xl border p-4 ${tone.card}`}>
      <div className="flex items-start gap-3">
        <div className={`shrink-0 h-10 w-10 rounded-xl ring-1 ${tone.chip} flex items-center justify-center`}>
          <i className={`fa-solid ${method.icon} text-lg`} aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2 flex-wrap">
            <code className="font-mono text-sm font-semibold text-ink">
              {method.wireMethod}
              {method.wireArgs ? (
                <span className="text-mist"> · {JSON.stringify(method.wireArgs)}</span>
              ) : null}
            </code>
            <span className={`text-[11px] uppercase tracking-wider font-medium ${tone.accent}`}>
              typical {method.typicalMs}
            </span>
          </div>
          <div className="mt-1 text-sm text-ink">{method.tagline}</div>
          <div className="mt-3 text-[11px] uppercase tracking-wider text-mist">Returns</div>
          <div className="mt-0.5 text-xs text-ink/80">{method.returns}</div>
        </div>
      </div>
    </div>
  );
}

function WirePreviewCard({ personLabel, method, ttlSec }) {
  if (!method) return null;
  // What the coord actually POSTs to the node. Personified so the admin
  // can compare "what I'm about to send" vs. what the response echoes.
  const preview = {
    person: personLabel || '<pick a target person>',
    method: method.wireMethod,
    ...(method.wireArgs ? { args: method.wireArgs } : {}),
    ttlSec: Number(ttlSec) || 30,
  };
  return (
    <div className="rounded-2xl border border-line bg-white p-4">
      <div className="text-[11px] uppercase tracking-wider text-mist mb-2">Wire preview</div>
      <pre className="rounded-lg bg-canvas p-3 text-[11px] font-mono text-ink overflow-x-auto whitespace-pre">
{JSON.stringify(preview, null, 2)}
      </pre>
      <div className="mt-2 text-[11px] text-mist">
        Coord POSTs this to <code className="font-mono text-ink/70">/api/rpc</code>.
        Node routes it via anchor WS (ONLINE proxy) or mesh envelope + persona-status
        proxy selection (MESH targets).
      </div>
    </div>
  );
}

function ResponsePanel({ resp, err }) {
  if (err) {
    return (
      <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="rounded-md bg-rose-200/70 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-rose-800">
            error
          </span>
          <code className="font-mono text-xs text-rose-800">{err.code || 'error'}</code>
        </div>
        <div className="mt-2 text-sm text-rose-900">{err.message}</div>
        {err.details ? (
          <pre className="mt-3 rounded-lg bg-rose-100 p-3 text-[11px] text-rose-900 overflow-auto max-h-64 whitespace-pre-wrap">
            {JSON.stringify(err.details, null, 2)}
          </pre>
        ) : null}
      </div>
    );
  }
  if (!resp) return null;
  const okTone = resp.ok
    ? 'border-emerald-200 bg-emerald-50'
    : 'border-amber-200 bg-amber-50';
  const chipOk = resp.ok
    ? 'bg-emerald-200/60 text-emerald-800'
    : 'bg-amber-200/60 text-amber-800';
  const path = resp.viaMesh ? 'mesh' : (resp.deliveredLive ? 'direct' : (resp.queued ? 'queued' : null));
  return (
    <div className={`rounded-2xl border p-4 ${okTone}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`rounded-md px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider ${chipOk}`}>
          {resp.ok ? 'ok' : 'terminal error'}
        </span>
        {path ? (
          <span className="rounded-md bg-slate-200/60 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-700">
            path: {path}
          </span>
        ) : null}
        {resp.polled ? (
          <span className="rounded-md bg-sky-200/60 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-sky-800">
            arrived via polling
          </span>
        ) : null}
        {resp.rpcId ? (
          <code className="ml-auto font-mono text-[11px] text-mist">
            id: {resp.rpcId.slice(0, 8)}
          </code>
        ) : null}
      </div>
      <div className="mt-2 flex items-center gap-4 text-[11px] text-mist">
        {resp.elapsedMs != null ? <span>Elapsed <span className="text-ink font-medium">{resp.elapsedMs} ms</span></span> : null}
        {resp.deliveredAt != null ? <span>Delivered at <span className="text-ink font-medium">{new Date(resp.deliveredAt).toLocaleTimeString()}</span></span> : null}
      </div>
      <div className="mt-3 text-[11px] uppercase tracking-wider text-mist">Result</div>
      <pre className="mt-1 rounded-lg bg-white/70 p-3 text-[11px] font-mono text-ink overflow-auto max-h-[420px] whitespace-pre-wrap">
        {JSON.stringify(resp.result ?? resp.error ?? resp, null, 2)}
      </pre>
    </div>
  );
}

export function RpcConsolePage({ currentUser }) {
  const toast = useToast();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [personUuid, setPersonUuid] = useState('');
  const [pickerId, setPickerId] = useState(METHODS[0].id);
  const [ttlSec, setTtlSec] = useState(30);
  const [dispatching, setDispatching] = useState(false);
  const [resp, setResp] = useState(null);
  const [err, setErr] = useState(null);
  // Etap 2 MESH follow-up (Round 1.493) — polling for late arrivals.
  const [pollingRpcId, setPollingRpcId] = useState(null);
  const [pollAttempts, setPollAttempts] = useState(0);
  // Copy-response acknowledgement (1.5s pulse).
  const [copied, setCopied] = useState(false);

  const isAdmin = currentUser?.role === 'admin';

  useEffect(() => {
    let alive = true;
    api.persons.list().then((data) => {
      if (!alive) return;
      const rows = Array.isArray(data?.persons) ? data.persons : (Array.isArray(data) ? data : []);
      rows.sort((a, b) => String(a.callsign || '').localeCompare(String(b.callsign || '')));
      setPersons(rows);
      setLoading(false);
    }).catch((e) => {
      toast?.push?.({ tone: 'rose', text: `Persons failed to load: ${e.message}` });
      setLoading(false);
    });
    return () => { alive = false; };
  }, []);

  const method = useMemo(
    () => METHODS.find((m) => m.id === pickerId) || METHODS[0],
    [pickerId],
  );

  const currentPerson = useMemo(
    () => persons.find((p) => p.uuid === personUuid) || null,
    [persons, personUuid],
  );

  const personLabel = useMemo(() => {
    if (!currentPerson) return '';
    return `${currentPerson.callsign || '(no callsign)'} · ${currentPerson.displayName || currentPerson.uuid.slice(0, 8)}`;
  }, [currentPerson]);

  const dispatch = useCallback(async () => {
    setErr(null);
    setResp(null);
    setPollingRpcId(null);
    setPollAttempts(0);
    if (!personUuid) {
      toast?.push?.({ tone: 'amber', text: 'Pick a target person first' });
      return;
    }
    setDispatching(true);
    try {
      // Wire args are picker-driven — no more free-form JSON to parse.
      const parsedArgs = method.wireArgs || null;
      const out = await api.rpc.dispatch(
        personUuid, method.wireMethod, parsedArgs, Number(ttlSec) || 30,
      );
      setResp(out);
      const ok = out?.ok;
      toast?.push?.({
        tone: ok ? 'emerald' : (out?.queued ? 'sky' : 'amber'),
        text: ok
          ? `Response in ${out.elapsedMs} ms`
          : (out?.queued ? 'Terminal offline — waiting for reconnect…' : 'Terminal returned an error'),
      });
      // Round 1.493 — 200 with viaMesh:true + code:'timeout_mesh_dispatch'
      // means node published via mesh but no proxy delivered the
      // response in the HTTP wait. Start polling to catch late
      // arrival. Direct-path timeout after delivery — also poll.
      if (out?.rpcId && (out.code === 'timeout_mesh_dispatch'
              || (out.deliveredLive && out.code === 'timeout_after_delivery'))) {
        setPollingRpcId(out.rpcId);
      }
    } catch (e) {
      const apiErr = e instanceof ApiError ? {
        code: e.code || 'error',
        message: e.message,
        details: e.details,
      } : { code: 'error', message: String(e) };
      setErr(apiErr);
      // 504 timeouts (both direct + mesh path) → start polling.
      if (apiErr.details?.rpcId
              && (apiErr.code === 'timeout_after_delivery'
                  || apiErr.code === 'timeout_mesh_dispatch')) {
        setPollingRpcId(apiErr.details.rpcId);
      }
      // 503 no_online_proxies — target is MESH-only AND no online
      // terminal can proxy for us. Nothing to poll.
      toast?.push?.({ tone: 'rose', text: apiErr.message });
    } finally {
      setDispatching(false);
    }
  }, [personUuid, method, ttlSec, toast]);

  // Round 1.493 — Etap 2 MESH proper. Poll GET /api/rpc/:rpcId every
  // 5 s if the initial POST timed out. Mesh dispatch typically returns
  // in 5-30 s; polling 2 min (24 × 5 s) covers cascade + retransmit.
  const MAX_POLL_ATTEMPTS = 24;
  const POLL_INTERVAL_MS = 5000;
  useEffect(() => {
    if (!pollingRpcId) return undefined;
    let alive = true;
    const timer = setInterval(async () => {
      if (!alive) return;
      setPollAttempts((n) => n + 1);
      try {
        const row = await api.rpc.getResult(pollingRpcId);
        if (!alive) return;
        if (!row) return;
        if (row.status === 'completed' || row.status === 'error') {
          setResp({
            rpcId:         row.rpcId,
            deliveredLive: row.deliveredAt != null,
            deliveredAt:   row.deliveredAt,
            ok:            row.status === 'completed',
            result:        row.status === 'completed' ? row.result : undefined,
            error:         row.status === 'error'     ? row.error  : undefined,
            elapsedMs:     row.elapsedMs,
            viaMesh:       row.viaMesh === true,
            polled:        true,
          });
          setErr(null);
          setPollingRpcId(null);
          toast?.push?.({
            tone: row.status === 'completed' ? 'emerald' : 'amber',
            text: row.status === 'completed'
              ? `Response arrived${row.elapsedMs != null ? ` (${row.elapsedMs} ms)` : ''}`
              : 'Terminal returned an error',
          });
        }
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) {
          setPollingRpcId(null);
          toast?.push?.({ tone: 'amber', text: 'RPC result expired' });
        }
      }
    }, POLL_INTERVAL_MS);
    return () => { alive = false; clearInterval(timer); };
  }, [pollingRpcId, toast]);

  useEffect(() => {
    if (pollAttempts >= MAX_POLL_ATTEMPTS && pollingRpcId) {
      setPollingRpcId(null);
      toast?.push?.({
        tone: 'amber',
        text: 'Terminal did not come back — polling stopped',
      });
    }
  }, [pollAttempts, pollingRpcId, toast]);

  if (!isAdmin) {
    return (
      <PageShell icon="fa-terminal" title="Diagnostic RPC" subtitle="Admin-only surface">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-900 max-w-2xl">
          <div className="text-lg font-medium">Access restricted</div>
          <div className="mt-1 text-sm">
            The RPC console is available only to admins. If you believe your role
            should permit it, contact your coordinator administrator.
          </div>
        </div>
      </PageShell>
    );
  }

  const canDispatch = !dispatching && !!personUuid;

  return (
    <PageShell
      icon="fa-terminal"
      title="Diagnostic RPC"
      subtitle="Dispatch diagnostic commands to a home terminal. ONLINE targets get a direct anchor-WS hop; MESH-only targets ride the routing engine via any online proxy."
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* ─────────── Left column — Request builder ─────────── */}
          <div className="lg:col-span-5 space-y-6">
            <div className="card p-6 space-y-5">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-xl bg-accent-soft text-accent flex items-center justify-center">
                  <i className="fa-solid fa-paper-plane text-base" aria-hidden="true" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-ink">Request</div>
                  <div className="text-xs text-mist">Pick a target and a method, then Dispatch.</div>
                </div>
              </div>

              {/* Target person */}
              <div>
                <label className="field-label flex items-center gap-2">
                  <i className="fa-solid fa-user-astronaut text-mist" aria-hidden="true" />
                  Target operator
                </label>
                <select
                  className="field"
                  value={personUuid}
                  onChange={(e) => setPersonUuid(e.target.value)}
                  disabled={loading || dispatching}
                >
                  <option value="">
                    {loading ? 'Loading…' : `Choose from ${persons.length} operator${persons.length === 1 ? '' : 's'}`}
                  </option>
                  {persons.map((p) => (
                    <option key={p.uuid} value={p.uuid}>
                      {p.callsign || '(no callsign)'} — {p.displayName || p.uuid.slice(0, 8)}
                    </option>
                  ))}
                </select>
                {currentPerson ? (
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-mist">
                    <i className="fa-solid fa-fingerprint" aria-hidden="true" />
                    <code className="font-mono">{currentPerson.uuid}</code>
                  </div>
                ) : null}
              </div>

              {/* Method picker with optgroups */}
              <div>
                <label className="field-label flex items-center gap-2">
                  <i className="fa-solid fa-code-branch text-mist" aria-hidden="true" />
                  Method
                </label>
                <select
                  className="field"
                  value={pickerId}
                  onChange={(e) => setPickerId(e.target.value)}
                  disabled={dispatching}
                >
                  {METHOD_GROUPS.map((grp) => (
                    <optgroup key={grp.label} label={grp.label}>
                      {grp.items.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label} — {m.tagline}
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </select>
              </div>

              {/* Wait time (TTL) */}
              <div>
                <label className="field-label flex items-center gap-2">
                  <i className="fa-solid fa-hourglass-half text-mist" aria-hidden="true" />
                  Wait time
                  <span className="text-mist font-normal">
                    · how long the coord blocks before returning
                  </span>
                </label>
                <div className="flex items-center gap-3">
                  <input
                    type="number"
                    min="1"
                    max="300"
                    className="field w-28 text-center"
                    value={ttlSec}
                    onChange={(e) => setTtlSec(e.target.value)}
                    disabled={dispatching}
                  />
                  <span className="text-sm text-mist">seconds</span>
                  <div className="ml-auto flex items-center gap-1.5">
                    {[15, 30, 60, 90].map((s) => (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setTtlSec(s)}
                        disabled={dispatching}
                        className={`rounded-lg px-2 py-1 text-xs font-medium transition-colors ${
                          Number(ttlSec) === s
                            ? 'bg-accent-soft text-accent ring-1 ring-accent/30'
                            : 'text-mist hover:bg-canvas'
                        }`}
                      >
                        {s}s
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dispatch button */}
              <div className="pt-2 flex items-center gap-3">
                <button
                  type="button"
                  onClick={dispatch}
                  disabled={!canDispatch}
                  className="btn-primary flex-1"
                >
                  {dispatching ? (
                    <>
                      <span className="loader-spinner" aria-hidden="true" />
                      Dispatching…
                    </>
                  ) : (
                    <>
                      <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                      Dispatch RPC
                    </>
                  )}
                </button>
                {resp || err ? (
                  <button
                    type="button"
                    onClick={() => { setResp(null); setErr(null); setPollingRpcId(null); setPollAttempts(0); }}
                    className="btn-secondary"
                    disabled={dispatching}
                  >
                    <i className="fa-solid fa-eraser" aria-hidden="true" />
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            {/* Method description card */}
            <MethodDescriptionCard method={method} />

            {/* Wire preview */}
            <WirePreviewCard
              personLabel={personLabel}
              method={method}
              ttlSec={ttlSec}
            />
          </div>

          {/* ─────────── Right column — Response ─────────── */}
          <div className="lg:col-span-7">
            <div className="card p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-9 w-9 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center">
                  <i className="fa-solid fa-inbox text-base" aria-hidden="true" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-ink">Response</div>
                  <div className="text-xs text-mist">
                    Direct-path targets return in &lt;1 s; mesh-path targets may take 5–30 s.
                  </div>
                </div>
                {pollingRpcId ? (
                  <div className="flex items-center gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-1.5 text-xs text-sky-800">
                    <span className="loader-spinner text-sky-600" aria-hidden="true" />
                    <span>Polling {pollAttempts}/{MAX_POLL_ATTEMPTS}</span>
                    <button
                      type="button"
                      onClick={() => { setPollingRpcId(null); setPollAttempts(0); }}
                      className="ml-1 rounded-md bg-sky-100 px-2 py-0.5 text-[10px] font-semibold text-sky-800 hover:bg-sky-200"
                    >
                      Stop
                    </button>
                  </div>
                ) : null}
                {(resp || err) ? (
                  <button
                    type="button"
                    title="Copy response to clipboard"
                    onClick={async () => {
                      const payload = err
                        ? JSON.stringify({ error: err.message || String(err) }, null, 2)
                        : JSON.stringify(resp, null, 2);
                      let okCopy = false;
                      try {
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          await navigator.clipboard.writeText(payload);
                          okCopy = true;
                        } else {
                          // Fallback for non-HTTPS / older browsers.
                          const ta = document.createElement('textarea');
                          ta.value = payload;
                          ta.style.position = 'fixed';
                          ta.style.left = '-9999px';
                          document.body.appendChild(ta);
                          ta.focus(); ta.select();
                          okCopy = document.execCommand('copy');
                          document.body.removeChild(ta);
                        }
                      } catch (e) { okCopy = false; }
                      if (okCopy) {
                        setCopied(true);
                        toast?.push?.({ tone: 'emerald', text: `Copied ${payload.length.toLocaleString()} chars to clipboard` });
                        window.setTimeout(() => setCopied(false), 1500);
                      } else {
                        toast?.push?.({ tone: 'rose', text: 'Clipboard write failed — permissions denied or insecure context' });
                      }
                    }}
                    className={`rounded-md border px-3 py-1.5 text-xs font-semibold flex items-center gap-1.5 transition-colors ${
                      copied
                        ? 'border-emerald-300 bg-emerald-50 text-emerald-800'
                        : 'border-line bg-white text-ink hover:bg-canvas'
                    }`}
                  >
                    <i className={copied ? 'fa-solid fa-check' : 'fa-regular fa-copy'} aria-hidden="true" />
                    {copied ? 'Copied' : 'Copy'}
                  </button>
                ) : null}
              </div>

              <ResponsePanel resp={resp} err={err} />

              {!resp && !err ? (
                <div className="rounded-2xl border border-dashed border-line p-8 text-center">
                  <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-canvas text-mist">
                    <i className="fa-solid fa-satellite-dish text-lg" aria-hidden="true" />
                  </div>
                  <div className="text-sm font-medium text-ink">Waiting for a Dispatch</div>
                  <div className="mt-1 text-xs text-mist">
                    Pick a target operator on the left and a method, then hit Dispatch.
                    The terminal's response will appear here.
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </PageShell>
  );
}
